import React, { useState, useEffect, useCallback } from 'react';
import { Button, VStack, HStack, Input, FormControl, FormLabel, Select,
  Box, Text, Badge, useToast, Flex, IconButton, Checkbox, InputGroup,
  InputLeftElement, Icon } from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiEdit2, FiSearch, FiRotateCcw, FiChevronDown, FiChevronUp, FiPackage, FiCopy } from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import PageShell from './PageShell';
import ConfirmModal from './ConfirmModal';

export default function ProductsModal({ isOpen, onClose, fullPage, onBack }) {
  const active = fullPage || isOpen;
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ item_code:'', nominal_code:'', description:'', default_net_price:'', default_vat_rate:'20' });
  const [formResources, setFormResources] = useState([]); // [{resource_id, duration_minutes}]
  const [codeError, setCodeError] = useState(false);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const [confirmDel, setConfirmDel] = useState(null);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [productReqs, setProductReqs] = useState([]);
  const [allResources, setAllResources] = useState([]);
  const [vatRates, setVatRates] = useState([]);

  const rowColor1 = user?.row_color_1 || '#FFFFFF';
  const rowColor2 = user?.row_color_2 || '#F7FAFC';

  const fetchProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      if (showDeleted) params.set('include_deleted', 'true');
      const { data } = await api.get(`/products?${params.toString()}`);
      setProducts(data);
    } catch(e) {}
  }, [query, showDeleted]);

  useEffect(() => { if (active) { fetchProducts(); fetchResources(); fetchVatRates(); setShowForm(false); setEditingId(null); } }, [isOpen, fetchProducts]); // eslint-disable-line

  const fetchResources = async () => { try { const { data } = await api.get('/resources'); setAllResources(data); } catch(e) {} };
  const fetchVatRates = async () => { try { const { data } = await api.get('/vat-rates'); setVatRates(data.filter(v => v.is_available)); } catch(e) {} };
  const fetchProductReqs = async (productId) => {
    try { const { data } = await api.get(`/resources/product-requirements/${productId}`); setProductReqs(data); } catch(e) { setProductReqs([]); }
  };
  const toggleExpand = (productId) => {
    if (expandedProduct === productId) { setExpandedProduct(null); setProductReqs([]); }
    else { setExpandedProduct(productId); fetchProductReqs(productId); }
  };
  const addReq = async (productId, resourceId, durationMins) => {
    try { await api.post('/resources/product-requirements', { product_id: productId, resource_id: resourceId, duration_minutes: durationMins || null }); fetchProductReqs(productId); } catch(e) {}
  };
  const removeReq = async (reqId) => {
    if (!expandedProduct) return;
    try { await api.delete(`/resources/product-requirements/${reqId}`); fetchProductReqs(expandedProduct); } catch(e) {}
  };

  const resetForm = () => { setForm({ item_code:'', nominal_code:'', description:'', default_net_price:'', default_vat_rate:'20' }); setFormResources([]); };
  const f = (k) => (e) => {
    if (k === 'item_code') setCodeError(false);
    setForm(p => ({ ...p, [k]: e.target.value }));
  };

  const toggleFormResource = (rid) => {
    setFormResources(prev => {
      const existing = prev.find(r => r.resource_id === rid);
      if (existing) return prev.filter(r => r.resource_id !== rid);
      return [...prev, { resource_id: rid, duration_minutes: 60 }];
    });
  };
  const setResDuration = (rid, mins) => {
    setFormResources(prev => prev.map(r => r.resource_id === rid ? { ...r, duration_minutes: mins } : r));
  };

  const handleSave = async () => {
    if (!form.description) { toast({ title:'Description required', status:'warning', duration:2000 }); return; }
    // Validate product code uniqueness
    if (form.item_code) {
      const duplicate = products.find(p =>
        p.item_code && p.item_code.toUpperCase() === form.item_code.toUpperCase()
        && p.id !== editingId && !p.deleted_at
      );
      if (duplicate) {
        setCodeError(true);
        toast({ title: 'You cannot use a product code on more than one product.', status: 'error', duration: 4000, isClosable: true });
        return;
      }
    }
    setCodeError(false);
    setSaving(true);
    try {
      let productId;
      if (editingId) { await api.put(`/products/${editingId}`, form); productId = editingId; }
      else { const { data } = await api.post('/products', form); productId = data.id; }
      // Save resource requirements
      if (productId && formResources.length > 0) {
        // Get existing and reconcile
        const { data: existing } = await api.get(`/resources/product-requirements/${productId}`);
        // Remove any not in formResources
        for (const ex of existing) {
          if (!formResources.find(r => r.resource_id === ex.resource_id)) {
            await api.delete(`/resources/product-requirements/${ex.id}`);
          }
        }
        // Add new ones
        for (const fr of formResources) {
          if (!existing.find(ex => ex.resource_id === fr.resource_id)) {
            await api.post('/resources/product-requirements', { product_id: productId, resource_id: fr.resource_id, duration_minutes: fr.duration_minutes });
          }
        }
      }
      await fetchProducts(); setShowForm(false); setEditingId(null); resetForm();
      toast({ title:'Saved', status:'success', duration:1500 });
    } catch(e) { toast({ title:'Failed', status:'error', duration:2000 }); }
    finally { setSaving(false); }
  };

  const handleDelete = (id) => setConfirmDel(id);
  const doDelete = async () => { try { await api.delete(`/products/${confirmDel}`); await fetchProducts(); toast({ title:'Deleted', status:'info', duration:1500 }); } catch(e){} finally { setConfirmDel(null); } };
  const handleUndelete = async (id) => { await api.post(`/products/${id}/undelete`); await fetchProducts(); toast({ title:'Product restored', status:'success', duration:1500 }); };

  const openEdit = async (p) => {
    setForm({ item_code:p.item_code||'', nominal_code:p.nominal_code||'', description:p.description||'',
      default_net_price:p.default_net_price||'', default_vat_rate:p.default_vat_rate||'20' });
    setEditingId(p.id); setShowForm(true);
    try { const { data } = await api.get(`/resources/product-requirements/${p.id}`); setFormResources(data.map(r => ({ resource_id: r.resource_id, duration_minutes: r.duration_minutes || 60 }))); } catch(e) { setFormResources([]); }
  };

  const openClone = async (p) => {
    setEditingId(null);
    setCodeError(false);
    let newCode = p.item_code ? p.item_code + '-COPY' : '';
    if (newCode) {
      let suffix = 1;
      while (products.find(x => x.item_code?.toUpperCase() === newCode.toUpperCase() && !x.deleted_at)) {
        suffix++;
        newCode = (p.item_code || '') + '-COPY' + (suffix > 1 ? suffix : '');
      }
    }
    setForm({
      item_code: newCode,
      nominal_code: p.nominal_code || '',
      description: (p.description || '') + ' (Copy)',
      default_net_price: p.default_net_price || '',
      default_vat_rate: p.default_vat_rate || '20',
    });
    try { const { data } = await api.get(`/resources/product-requirements/${p.id}`); setFormResources(data.map(r => ({ resource_id: r.resource_id, duration_minutes: r.duration_minutes || 60 }))); } catch(e) { setFormResources([]); }
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const headerBtn = <Button size="xs" leftIcon={<FiPlus />} colorScheme="blue" variant="outline"
    onClick={() => { resetForm(); setEditingId(null); setShowForm(!showForm); }}>{showForm ? 'Cancel' : 'Add'}</Button>;

  const fmtDuration = (mins) => {
    if (!mins) return '';
    if (mins >= 1440) return `${mins / 1440} day${mins > 1440 ? 's' : ''}`;
    if (mins >= 60) return `${mins / 60} hr${mins > 60 ? 's' : ''}`;
    return `${mins} min`;
  };

  return (
    <>
    <PageShell fullPage={fullPage} onBack={onBack} isOpen={isOpen} onClose={onClose} title="Products &amp; Services" headerRight={headerBtn}>
      <Box maxW="750px" mx="auto">
          <HStack mb={3} spacing={2}>
            <InputGroup size="xs"><InputLeftElement><Icon as={FiSearch} color="gray.400" boxSize={3} /></InputLeftElement>
              <Input pl={7} placeholder="Search products..." value={query} onChange={e => { setQuery(e.target.value); }} /></InputGroup>
            <Checkbox size="sm" isChecked={showDeleted} onChange={e => setShowDeleted(e.target.checked)}>
              <Text fontSize="10px" whiteSpace="nowrap">Show Deleted</Text></Checkbox>
          </HStack>

          {showForm && (
            <Box bg="blue.50" p={3} borderRadius="md" mb={4}>
              <VStack spacing={2}>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={3} isInvalid={codeError}><FormLabel fontSize="xs" mb={0.5} color={codeError ? 'red.500' : undefined}>Item Code {codeError && <Text as="span" fontSize="9px" color="red.500">(already in use)</Text>}</FormLabel>
                    <Input size="xs" value={form.item_code} onChange={f('item_code')} placeholder="e.g. PROD-001"
                      borderColor={codeError ? 'red.400' : undefined}
                      _focus={{ borderColor: codeError ? 'red.500' : 'blue.400' }}
                    /></FormControl>
                  <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Nominal Code</FormLabel>
                    <Input size="xs" value={form.nominal_code} onChange={f('nominal_code')} /></FormControl>
                </HStack>
                <FormControl><FormLabel fontSize="xs" mb={0.5}>Description *</FormLabel>
                  <Input size="sm" value={form.description} onChange={f('description')} /></FormControl>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Default Net Price £</FormLabel>
                    <Input size="xs" type="number" step="0.01" value={form.default_net_price} onChange={f('default_net_price')} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Default VAT</FormLabel>
                    <Select size="xs" value={form.default_vat_rate} onChange={f('default_vat_rate')}>
                      {vatRates.map(v => <option key={v.id} value={v.rate}>{v.code || v.name} ({v.rate}%)</option>)}
                      {vatRates.length === 0 && <><option value="20">Standard (20%)</option><option value="5">Reduced (5%)</option><option value="0">Zero (0%)</option></>}
                    </Select></FormControl>
                </HStack>

                {/* Resource requirements inline */}
                <FormControl>
                  <FormLabel fontSize="xs" mb={0.5}><FiPackage style={{ display:'inline', marginRight:4 }} />Resource Requirements</FormLabel>
                  {formResources.length > 0 && (
                    <VStack spacing={1} align="stretch" mb={2}>
                      {formResources.map(fr => {
                        const res = allResources.find(r => r.id === fr.resource_id);
                        return (
                          <Flex key={fr.resource_id} bg="teal.50" px={2} py={1} borderRadius="sm" align="center" justify="space-between">
                            <HStack spacing={2} fontSize="11px">
                              <Badge colorScheme={res?.is_person ? 'purple' : 'teal'} fontSize="8px">
                                {res?.is_person ? '👤' : '📦'} {res?.name || '?'}
                              </Badge>
                            </HStack>
                            <HStack spacing={1}>
                              <Select size="xs" w="90px" value={fr.duration_minutes}
                                onChange={e => setResDuration(fr.resource_id, parseInt(e.target.value))}>
                                <option value="30">30 min</option><option value="60">1 hour</option>
                                <option value="120">2 hours</option><option value="180">3 hours</option>
                                <option value="240">4 hours</option><option value="480">Full day</option>
                                <option value="1440">1 day</option><option value="2880">2 days</option>
                              </Select>
                              <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                                onClick={() => toggleFormResource(fr.resource_id)} aria-label="Remove" />
                            </HStack>
                          </Flex>
                        );
                      })}
                    </VStack>
                  )}
                  <Flex gap={1} flexWrap="wrap">
                    {allResources.filter(r => !formResources.find(fr => fr.resource_id === r.id)).map(r => (
                      <Badge key={r.id} cursor="pointer" fontSize="9px" px={2} py={0.5}
                        colorScheme={r.is_person ? 'purple' : 'teal'} variant="outline"
                        onClick={() => toggleFormResource(r.id)}>
                        + {r.is_person ? '👤 ' : ''}{r.resource_code ? `${r.resource_code} — ` : ''}{r.name}
                      </Badge>
                    ))}
                  </Flex>
                </FormControl>

                <Button size="xs" bg="brand.600" color="white" _hover={{ bg:'brand.700' }}
                  onClick={handleSave} isLoading={saving} alignSelf="flex-end">{editingId ? 'Update' : 'Add Product'}</Button>
              </VStack>
            </Box>
          )}

          {products.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>No products found.</Text>
          ) : (
            <VStack spacing={0} align="stretch">
              {products.map((p, idx) => (
                <Box key={p.id} bg={p.deleted_at ? 'red.50' : (idx % 2 === 0 ? rowColor1 : rowColor2)}>
                <Flex p={2} border="1px" borderColor={p.deleted_at ? 'red.200' : 'gray.100'} borderRadius="md"
                  align="center" justify="space-between" opacity={p.deleted_at ? 0.5 : 1}>
                  <Box flex={1}>
                    <Flex align="center" gap={2}>
                      {p.item_code && <Badge fontSize="8px" colorScheme="gray">{p.item_code}</Badge>}
                      <Text fontSize="12px" fontWeight="600">{p.description}</Text>
                      {!!p.deleted_at && <Badge fontSize="8px" colorScheme="red">DELETED</Badge>}
                    </Flex>
                    <HStack fontSize="10px" color="gray.500" spacing={2} mt={0.5}>
                      <Text>£{parseFloat(p.default_net_price||0).toFixed(2)}</Text>
                      <Text>VAT {p.default_vat_rate}%</Text>
                      {p.nominal_code && <Text>Nom: {p.nominal_code}</Text>}
                    </HStack>
                  </Box>
                  <HStack spacing={0}>
                    {!p.deleted_at ? (
                      <>
                        <IconButton icon={expandedProduct === p.id ? <FiChevronUp /> : <FiChevronDown />} size="xs" variant="ghost"
                          onClick={() => toggleExpand(p.id)} aria-label="Resources" title="Resource requirements" />
                        <IconButton icon={<FiEdit2 />} size="xs" variant="ghost" onClick={() => openEdit(p)} aria-label="Edit" />
                        <IconButton icon={<FiCopy />} size="xs" variant="ghost" colorScheme="teal" onClick={() => openClone(p)} aria-label="Clone product" title="Clone this product" />
                        <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red" onClick={() => handleDelete(p.id)} aria-label="Delete" />
                      </>
                    ) : (
                      <IconButton icon={<FiRotateCcw />} size="xs" variant="ghost" colorScheme="blue" onClick={() => handleUndelete(p.id)} aria-label="Restore" />
                    )}
                  </HStack>
                </Flex>
                {/* Resource requirements panel */}
                {expandedProduct === p.id && !p.deleted_at && (
                  <Box mx={2} mb={2} pt={2} borderTop="1px" borderColor="gray.200">
                    <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase" mb={1}>Resource Requirements</Text>
                    {productReqs.length > 0 ? (
                      <VStack spacing={1} align="stretch" mb={2}>
                        {productReqs.map(req => (
                          <Flex key={req.id} align="center" justify="space-between" bg="teal.50" px={2} py={1} borderRadius="sm">
                            <HStack spacing={2} fontSize="11px">
                              <Badge colorScheme={req.is_person ? 'purple' : 'teal'} fontSize="8px">
                                {req.is_person ? '👤' : '📦'} {req.resource_name || req.type_name || 'Any'}
                              </Badge>
                              {req.resource_code && <Text color="gray.500">({req.resource_code})</Text>}
                              {req.duration_minutes && <Badge fontSize="7px" colorScheme="gray">{fmtDuration(req.duration_minutes)}</Badge>}
                              {!req.is_required && <Badge fontSize="7px" colorScheme="yellow">Optional</Badge>}
                            </HStack>
                            <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                              onClick={() => removeReq(req.id)} aria-label="Remove" />
                          </Flex>
                        ))}
                      </VStack>
                    ) : <Text fontSize="10px" color="gray.400" mb={2}>No resource requirements</Text>}
                    <Flex gap={1} flexWrap="wrap">
                      {allResources.filter(r => !productReqs.some(req => req.resource_id === r.id)).map(r => (
                        <Badge key={r.id} cursor="pointer" fontSize="9px" px={2} py={0.5}
                          colorScheme={r.is_person ? 'purple' : 'teal'} variant="outline"
                          onClick={() => addReq(p.id, r.id, 60)}>
                          + {r.is_person ? '👤 ' : ''}{r.resource_code ? `${r.resource_code} — ` : ''}{r.name}
                        </Badge>
                      ))}
                    </Flex>
                  </Box>
                )}
                </Box>
              ))}
            </VStack>
          )}
      </Box>
    </PageShell>
    <ConfirmModal isOpen={!!confirmDel} onClose={() => setConfirmDel(null)}
      title="Delete Product" message="Are you sure you want to delete this product?"
      confirmLabel="Delete" confirmColor="red" onConfirm={doDelete} />
    </>
  );
}
